import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Minus, ShoppingBag, Check, Waves, Loader2, Utensils, Wine, CreditCard, BellRing } from "lucide-react";
import { ItemOptionsDialog } from "./ItemOptionsDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const stationFor = (group) => (group === "Food" ? "kitchen" : "bar");

const STEPS = [
  { key: "new", label: "Received" },
  { key: "preparing", label: "Preparing" },
  { key: "served", label: "Served" },
];

const StationStatus = ({ order }) => {
  const isFood = order.station === "kitchen";
  const curIdx = STEPS.findIndex((s) => s.key === order.status);
  return (
    <div className="w-full rounded-2xl border border-sand-300 bg-white p-4" data-testid={`ticket-station-${order.station}`}>
      <p className={`mb-3 flex items-center gap-1.5 text-sm font-semibold ${isFood ? "text-amber-600" : "text-sea"}`}>
        {isFood ? <Utensils size={15} /> : <Wine size={15} />} {isFood ? "Food" : "Drinks"} · from the {isFood ? "kitchen" : "bar"}
      </p>
      <div className="flex items-center">
        {STEPS.map((s, i) => {
          const done = i <= curIdx;
          return (
            <div key={s.key} className="flex flex-1 flex-col items-center" data-testid={`ticket-step-${order.station}-${s.key}`}>
              <div className="flex w-full items-center">
                <div className={`h-1 flex-1 rounded ${i === 0 ? "opacity-0" : done ? "bg-brand" : "bg-sand-300"}`} />
                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${done ? "bg-brand text-white" : "bg-white text-slate-400 ring-1 ring-sand-300"}`}>{i + 1}</div>
                <div className={`h-1 flex-1 rounded ${i === STEPS.length - 1 ? "opacity-0" : i < curIdx ? "bg-brand" : "bg-sand-300"}`} />
              </div>
              <span className={`mt-1.5 text-[11px] font-semibold ${done ? "text-brand-700" : "text-slate-400"}`}>{s.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function MenuPage() {
  const { spotId } = useParams();
  const [menu, setMenu] = useState([]);
  const [cart, setCart] = useState([]);
  const [note, setNote] = useState("");
  const [allergies, setAllergies] = useState("");
  const [dialogCtx, setDialogCtx] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [ticketOrders, setTicketOrders] = useState([]);
  const [spotName, setSpotName] = useState(null);
  const [calling, setCalling] = useState(null);
  const [payPrompt, setPayPrompt] = useState(false);
  const [rules, setRules] = useState(null);

  useEffect(() => {
    axios.get(`${API}/menu`).then((r) => { setMenu(r.data); setActiveGroup(r.data[0]?.group || null); });
    axios.get(`${API}/option-rules`).then((r) => setRules(r.data)).catch(() => {});
    axios.get(`${API}/lounges`).then((r) => { const l = r.data.find((x) => x.id === spotId); if (l) setSpotName(l.label); }).catch(() => {});
  }, [spotId]);

  useEffect(() => {
    if (!ticket) return;
    const poll = () => axios.get(`${API}/orders/ticket/${ticket}`)
      .then((r) => setTicketOrders(r.data)).catch(() => {});
    poll();
    const t = setInterval(poll, 6000);
    return () => clearInterval(t);
  }, [ticket]);

  const spotLabel = spotName
    || (spotId?.startsWith("umbrella") ? `Umbrella ${spotId.split("-")[1]}`
    : spotId?.startsWith("villa") ? `Villa ${spotId.split("-")[1]}`
    : `Gazebo ${spotId?.split("-")[1] ?? spotId}`);

  const openItem = (item, cat) => setDialogCtx({ item, category: cat.category, group: cat.group, station: stationFor(cat.group) });
  const addLine = (line) => setCart((c) => [...c, { ...line, uid: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` }]);
  const changeQty = (uid, d) => setCart((c) => c.flatMap((l) => (l.uid === uid ? (l.qty + d <= 0 ? [] : [{ ...l, qty: l.qty + d }]) : [l])));

  const lines = cart;
  const count = lines.reduce((s, l) => s + l.qty, 0);
  const total = lines.reduce((s, l) => s + l.price * l.qty, 0);

  const callWaiter = async (type, payMethod) => {
    setCalling(type);
    try {
      await axios.post(`${API}/calls`, { spot_id: spotId, spot_label: spotLabel, type, pay_method: payMethod });
      toast.success("A waiter has been notified", { description: payMethod ? `${spotLabel} · paying by ${payMethod}` : spotLabel });
      setPayPrompt(false);
    } catch { toast.error("Couldn't reach a waiter — please try again"); }
    finally { setCalling(null); }
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        spot_id: spotId, spot_label: spotLabel, note, allergies,
        items: lines.map((l) => ({ name: l.name, price: l.price, qty: l.qty, station: l.station, options: l.options || null, note: l.note || "" })),
      });
      setTicket(data.ticket); setTicketOrders(data.orders || []);
      setSent(true); setCart([]); setNote(""); setAllergies("");
    } catch { toast.error("Couldn't send your order — please try again"); } finally { setSending(false); }
  };

  const groups = useMemo(() => {
    const seen = [];
    menu.forEach((m) => { const g = m.group || "More"; if (!seen.includes(g)) seen.push(g); });
    return seen;
  }, [menu]);
  const groupCats = useMemo(() => menu.filter((m) => (m.group || "More") === activeGroup), [menu, activeGroup]);

  if (sent) {
    const anyServed = ticketOrders.length > 0 && ticketOrders.every((o) => o.status === "served");
    return (
      <div className="flex min-h-screen flex-col items-center bg-sand-100 px-6 pt-16 text-center">
        <div className={`flex h-16 w-16 items-center justify-center rounded-full ${anyServed ? "bg-sage/20 text-sage" : "bg-brand/15 text-brand-700"}`} data-testid="order-sent">
          <Check size={32} />
        </div>
        <h1 className="mt-5 font-display text-3xl tracking-wide text-slate-900">{anyServed ? "Enjoy!" : "Order received!"}</h1>
        <p className="mt-2 text-slate-500">For <b>{spotLabel}</b></p>

        <div className="mt-6 w-full max-w-sm space-y-3" data-testid="order-status-stepper">
          {ticketOrders.map((o) => <StationStatus key={o.id} order={o} />)}
        </div>
        {ticketOrders.length > 1 && (
          <p className="mt-3 max-w-sm text-xs text-slate-400">Your drinks and food are prepared separately, so they may arrive at different times.</p>
        )}

        <button data-testid="order-again-btn" onClick={() => { setSent(false); setTicket(null); setTicketOrders([]); }}
          className="mt-8 rounded-full bg-brand px-6 py-3 font-semibold text-white hover:bg-brand-600">Order something else</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-100 pb-28">
      <header className="sticky top-0 z-20 border-b border-sand-300 bg-sand-50/90 px-4 py-3 backdrop-blur">
        <div className="flex items-center gap-2">
          <Waves className="text-sea" size={22} />
          <div>
            <h1 className="font-display text-xl leading-none tracking-wide text-slate-900">TRIBE Beach</h1>
            <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500">Menu · {spotLabel}</p>
          </div>
        </div>
        {/* Call-a-waiter buttons */}
        <div className="mt-3 grid grid-cols-3 gap-2" data-testid="call-buttons">
          <button data-testid="call-order" onClick={() => callWaiter("order")} disabled={!!calling}
            className="flex flex-col items-center justify-center gap-1 rounded-xl border border-sea/30 bg-white px-2 py-2.5 text-center text-[12px] font-bold leading-tight text-sea transition-colors hover:bg-sea/5 active:bg-sea/10 disabled:opacity-50">
            {calling === "order" ? <Loader2 size={18} className="animate-spin" /> : <ShoppingBag size={18} />}
            I want to order
          </button>
          <button data-testid="call-pay" onClick={() => setPayPrompt((v) => !v)} disabled={!!calling}
            className={`flex flex-col items-center justify-center gap-1 rounded-xl border bg-white px-2 py-2.5 text-center text-[12px] font-bold leading-tight text-sea transition-colors hover:bg-sea/5 active:bg-sea/10 disabled:opacity-50 ${payPrompt ? "border-sea ring-2 ring-sea/40" : "border-sea/30"}`}>
            {calling === "pay" ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
            Pay
          </button>
          <button data-testid="call-waiter" onClick={() => callWaiter("waiter")} disabled={!!calling}
            className="flex flex-col items-center justify-center gap-1 rounded-xl border border-sea/30 bg-white px-2 py-2.5 text-center text-[12px] font-bold leading-tight text-sea transition-colors hover:bg-sea/5 active:bg-sea/10 disabled:opacity-50">
            {calling === "waiter" ? <Loader2 size={18} className="animate-spin" /> : <BellRing size={18} />}
            Call waiter
          </button>
        </div>
        {payPrompt && (
          <div className="mt-2 flex items-center gap-2 rounded-xl bg-sea/5 p-2" data-testid="pay-method-prompt">
            <span className="pl-1 text-xs font-semibold text-slate-600">How will you pay?</span>
            <button data-testid="pay-cash" onClick={() => callWaiter("pay", "Cash")} disabled={!!calling}
              className="flex-1 rounded-lg bg-white py-2 text-sm font-bold text-slate-700 ring-1 ring-sand-300 transition-colors hover:bg-sea/10 disabled:opacity-50">Cash</button>
            <button data-testid="pay-card" onClick={() => callWaiter("pay", "Card")} disabled={!!calling}
              className="flex-1 rounded-lg bg-white py-2 text-sm font-bold text-slate-700 ring-1 ring-sand-300 transition-colors hover:bg-sea/10 disabled:opacity-50">Card</button>
          </div>
        )}
        {/* Level 1: groups */}
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="menu-groups">
          {groups.map((g) => (
            <button key={g} data-testid={`menu-group-${g}`} onClick={() => setActiveGroup(g)}
              className={`shrink-0 rounded-full px-4 py-2 text-sm font-bold transition-colors ${activeGroup === g ? "bg-brand text-white shadow" : "bg-white text-slate-700 ring-1 ring-sand-300"}`}>
              {g}
            </button>
          ))}
        </div>
        {/* Level 2: categories inside the active group */}
        {groupCats.length > 1 && (
          <div className="mt-2 flex gap-2 overflow-x-auto pb-1" data-testid="menu-cats">
            {groupCats.map((c) => (
              <button key={c.category} onClick={() => document.getElementById(`cat-${c.category}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="shrink-0 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 ring-1 ring-sand-300">
                {c.category}
              </button>
            ))}
          </div>
        )}
      </header>

      {menu.length === 0 && <div className="flex justify-center py-20 text-slate-400"><Loader2 className="animate-spin" /></div>}

      <main className="mx-auto max-w-2xl px-4">
        {groupCats.map((cat) => (
          <section key={cat.category} id={`cat-${cat.category}`} className="scroll-mt-40 pt-6">
            <div className="mb-3 overflow-hidden rounded-2xl">
              <div className="relative h-28">
                <img src={cat.image} alt={cat.category} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <h2 className="absolute bottom-2 left-3 font-display text-2xl tracking-wide text-white">{cat.category}</h2>
              </div>
            </div>
            <div className="space-y-1.5">
              {cat.items.map((item) => {
                const inCart = cart.filter((l) => l.name === item.name).reduce((s, l) => s + l.qty, 0);
                return (
                  <div key={item.name} data-testid={`menu-item-${item.name}`} className="flex items-start gap-3 rounded-xl bg-white/70 p-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-800">{item.name}</p>
                      {item.desc && <p className="mt-0.5 text-xs leading-snug text-slate-500">{item.desc}</p>}
                      <p className="mt-1 text-sm font-bold text-brand-700">{item.price.toFixed(2)}€</p>
                    </div>
                    <button data-testid={`item-add-${item.name}`} onClick={() => openItem(item, cat)}
                      className="flex h-8 shrink-0 items-center gap-1 rounded-full bg-slate-900 px-3 text-xs font-semibold text-white transition-colors hover:bg-slate-700">
                      <Plus size={14} /> Add{inCart > 0 ? ` · ${inCart}` : ""}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </main>

      {count > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-sand-300 bg-white/95 p-3 backdrop-blur">
          <div className="mx-auto max-w-2xl">
            <div className="mb-2 max-h-32 space-y-1 overflow-y-auto" data-testid="cart-review">
              {lines.map((l) => (
                <div key={l.uid} className="flex items-start gap-2 text-sm">
                  <div className="flex items-center gap-1.5 rounded-full bg-sand-100 p-0.5">
                    <button data-testid={`cart-minus-${l.uid}`} onClick={() => changeQty(l.uid, -1)} className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-brand-700"><Minus size={13} /></button>
                    <span className="w-4 text-center text-xs font-bold">{l.qty}</span>
                    <button onClick={() => changeQty(l.uid, 1)} className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-brand-700"><Plus size={13} /></button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="font-semibold text-slate-800">{l.name}</span>
                    {l.options?.length ? <span className="text-slate-500"> · {l.options.join(" · ")}</span> : null}
                    {l.note ? <span className="italic text-slate-400"> — {l.note}</span> : null}
                  </div>
                  <span className="shrink-0 text-slate-400">{(l.price * l.qty).toFixed(2)}€</span>
                </div>
              ))}
            </div>
            <input data-testid="order-allergies" value={allergies} onChange={(e) => setAllergies(e.target.value)}
              placeholder="Any allergies? (leave blank if none)"
              className="mb-2 h-10 w-full rounded-lg border border-destructive/40 bg-destructive/5 px-3 text-sm outline-none focus:border-destructive" />
            <input data-testid="order-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Comments for the order (optional)…"
              className="mb-2 h-10 w-full rounded-lg border border-sand-300 bg-sand-50 px-3 text-sm outline-none focus:border-brand" />
            <button data-testid="send-order-btn" onClick={send} disabled={sending}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand text-base font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-60">
              {sending ? <Loader2 className="animate-spin" size={18} /> : <ShoppingBag size={18} />}
              Send order · {count} item{count > 1 ? "s" : ""} · {total.toFixed(2)}€
            </button>
          </div>
        </div>
      )}
      <ItemOptionsDialog ctx={dialogCtx} rules={rules} onAdd={addLine} onClose={() => setDialogCtx(null)} />
    </div>
  );
}
